// @ts-check

/**
 * @file Renders the shared authenticated board layout.
 */

import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import {Container as FlashNotificationsContainer, FlashNotifications} from "flash-notifications"
import gettextConfig from "gettext-universal/build/src/config.js"
import TranslateContext from "gettext-universal/build/src/translate-context.js"
import {WithDefaultStyle} from "@kaspernj/api-maker/build/utils/default-style"
import {Link, useRouter} from "expo-router"
import {memo, useRef} from "react"
import useLocationPath from "on-location-changed/build/use-location-path"
import {useSafeAreaInsets} from "react-native-safe-area-context"
import usePressOutside from "outside-eye/build/use-press-outside.js"
import usePressOutsideProps from "outside-eye/build/use-press-outside-props.js"
import useBreakpoint from "responsive-breakpoints/build/use-breakpoint.js"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View as PropforgeView} from "react-native-propforge"
import {AppState, Pressable, ScrollView, Text, View} from "react-native"
import ConfirmModalHost from "../confirm-modal-host"
import LoadingState from "../loading-state"
import SignInForm from "../../auth/sign-in-form"
import {User} from "../../frontend-models/user"
import useCurrentUser, {updateCurrentUser} from "../../auth/use-current-user"
import {useAggregateScreenLoading} from "./screen-loading"
import {applyLocale} from "../../translations/initialize"
import {locationPathWithQueryParams} from "on-location-changed/build/query-param-updates"
import useCurrentQueryParams from "on-location-changed/build/use-current-query-params"
import {signOut as deviseSignOut} from "../../shared/auth"
import {buildBoardThemeColors, detectSystemTheme} from "../../shared/theme"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import ProjectSyncStatusIndicator from "../project-sync-status-indicator"
import {useProjectSyncActivityIndicator} from "../../sync/project-sync-activity-indicator"
import {useProjectSyncLifecycle} from "../../sync/project-sync-lifecycle"
import {optionalRouteParam} from "../../shared/route-params"

/** @import {NamedExoticComponent, ReactNode, RefObject} from "react" */
/** @import {CurrentUserData} from "../../auth/use-current-user" */
/** @typedef {Record<string, string | string[] | undefined>} RouteQueryParams */
/** @typedef {{locale: string | null}} BoardLayoutTranslateProviderValue */
/** @type {Record<string, object>} */
const styles = {}
/** @type {Record<string, object>} */
const dataSets = {}
const seenBoardLayoutDebugKeys = new Set()

/** @typedef {import("react").ComponentProps<typeof FontAwesome6>["name"]} FontAwesome6Name */

const LOCALE_OPTIONS = [
  {label: "EN", value: "en"},
  {label: "DA", value: "da"},
  {label: "DE", value: "de"}
]
export const BOARD_LAYOUT_SIDEBAR_WIDTH = 220

// Routes that must render even when no user is signed in. The
// system-testing runner lands on /blank as its idle page.
const PUBLIC_ROUTE_PREFIXES = ["/blank"]

/**
 * Checks whether a route can render without a signed-in user.
 * @param {string} pathname Current route pathname.
 * @returns {boolean} True when the route is public.
 */
function isPublicRoute(pathname) {
  for (const prefix of PUBLIC_ROUTE_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return true
  }
  return false
}

/**
 * Checks whether the current route owns the standalone sign-in screen.
 * @param {string} pathname Current route pathname.
 * @returns {boolean} True for the sign-in route.
 */
function isSignInRoute(pathname) {
  return pathname === "/users/sign-in"
}

// Routes that manage their own scroll context — the layout must
// not wrap their content in a body-level ScrollView, or the inner
// scroll regions and absolutely-positioned overlay modals end up
// nested inside an outer scroller.
const NO_BODY_SCROLL_PATTERNS = [
  /^\/$/,
  /^\/tasks\/new$/,
  /^\/tasks\/[^/]+\/edit$/,
  /^\/timelogs\/dashboard$/
]

const PROJECT_BOARD_ROUTE_PATTERN = /^\/projects\/[^/]+\/boards\/[^/]+$/

const FULL_BLEED_BODY_SCROLL_PATTERNS = [
  PROJECT_BOARD_ROUTE_PATTERN
]

/**
 * Checks whether the layout should wrap the active route in a
 * body-level ScrollView. Defaults to true; specific routes that
 * manage their own scroll opt out via {@link NO_BODY_SCROLL_PATTERNS}.
 * @param {string} pathname Current route pathname.
 * @returns {boolean} True when the body should be scrollable.
 */
function shouldScrollBody(pathname) {
  return !NO_BODY_SCROLL_PATTERNS.some((pattern) => pattern.test(pathname))
}

/**
 * Checks whether the shared body scroller should avoid its default padding.
 * @param {string} pathname Current route pathname.
 * @returns {boolean} True when the child screen owns its spacing.
 */
function shouldUseFullBleedBodyScroll(pathname) {
  return FULL_BLEED_BODY_SCROLL_PATTERNS.some((pattern) => pattern.test(pathname))
}

/**
 * Emits focused BoardLayout diagnostics during system-test debugging.
 * @param {string} key Unique state snapshot key.
 * @param {string} message Short event label.
 * @param {Record<string, unknown>} [details] Structured fields for the event.
 * @returns {void} Nothing.
 */
function boardLayoutDebugLogOnce(key, message, details = {}) {
  if (seenBoardLayoutDebugKeys.has(key)) return
  if (typeof window === "undefined" || !window.location) return
  if (!window.location.search.includes("systemTestDebug=true")) return

  seenBoardLayoutDebugKeys.add(key)
  console.log("[awesome_tasks board-layout]", message, {
    href: window.location.href,
    ...details
  })
}

/**
 * Checks whether a nav item can render for the current abilities.
 * @param {Record<string, boolean>} abilities Current user ability map.
 * @param {string | undefined} requiresAbility Ability key required by the nav item.
 * @returns {boolean} True when the item can render.
 */
function canShowNavAbility(abilities, requiresAbility) {
  return !requiresAbility || abilities[requiresAbility] === true
}

/**
 * Checks whether a navigation item should be active for the current route.
 * @param {string} pathname Current route pathname.
 * @param {string} href Route path represented by the item.
 * @param {string} [excludedPrefix] Prefix that must not activate this item.
 * @returns {boolean} True when this item owns the active route.
 */
function navItemActive(pathname, href, excludedPrefix) {
  if (excludedPrefix && (pathname === excludedPrefix || pathname.startsWith(`${excludedPrefix}/`))) return false
  return pathname === href || pathname.startsWith(`${href}/`)
}

/** @typedef {"bottom" | "mobile" | "sidebar"} BoardLayoutNavLocation */

/**
 * @typedef {object} BoardLayoutNavItemProps
 * @property {boolean} active Whether the item represents the active route.
 * @property {string} href Route path to open.
 * @property {FontAwesome6Name} icon FontAwesome icon name.
 * @property {string} label Translated navigation label.
 * @property {string} linkTestID Stable test id for the wrapped link.
 * @property {BoardLayoutNavLocation} location Navigation surface to render for.
 * @property {() => unknown} [onPress] Optional press handler.
 * @property {string} testID Stable test id for the pressable item.
 * @property {"dark" | "light"} themeName Active theme name.
 */

/** @typedef {Record<string, never>} BoardLayoutNavItemState */

/**
 * Shared navigation item used by sidebar, bottom nav, and mobile drawer.
 * @augments {ShapeComponent<BoardLayoutNavItemProps, BoardLayoutNavItemState>}
 */
class BoardLayoutNavItemClass extends ShapeComponent {
  static defaultProps = {
    onPress: undefined
  }

  /**
   * Renders one navigation item.
   * @returns {ReactNode} Navigation item link.
   */
  render() {
    const {active, href, icon, label, linkTestID, location, onPress, testID, themeName} = this.p
    const colors = buildBoardThemeColors(themeName)
    if (location === "bottom") {
      return (
        <Link
          asChild
          href={href}
          testID={linkTestID}
        >
          <Pressable
            aria-current={active ? "page" : undefined}
            style={styles[`bottomNavItem-${themeName}-${active}`] ||= {
              alignItems: "center",
              flex: 1,
              gap: 2,
              paddingVertical: 6
            }}
            testID={testID}
          >
            <FontAwesome6
              color={active ? colors.sidebarActiveText : colors.sidebarMutedText}
              name={icon}
              size={18}
            />
            <Text
              style={styles[`bottomNavItemText-${themeName}-${active}`] ||= {
                color: active ? colors.sidebarActiveText : colors.sidebarMutedText,
                fontSize: 10,
                fontWeight: 700
              }}
              testID={`${testID}/label`}
            >
              {label}
            </Text>
          </Pressable>
        </Link>
      )
    }
    if (location === "mobile") {
      return (
        <Link
          asChild
          href={href}
          testID={linkTestID}
        >
          <Pressable
            aria-current={active ? "page" : undefined}
            onPress={onPress}
            style={styles[`mobileNavItem-${active}`] ||= {
              alignItems: "center",
              backgroundColor: active ? "rgba(29, 78, 216, 0.2)" : "transparent",
              borderRadius: 10,
              flexDirection: "row",
              gap: 12,
              marginBottom: 4,
              paddingHorizontal: 12,
              paddingVertical: 12
            }}
            testID={testID}
          >
            <FontAwesome6
              color={active ? "#93c5fd" : "#94a3b8"}
              name={icon}
              size={16}
            />
            <Text
              style={styles[`mobileNavItemText-${active}`] ||= {
                color: active ? "#93c5fd" : "#e2e8f0",
                fontSize: 14,
                fontWeight: 600
              }}
              testID={`${testID}/label`}
            >
              {label}
            </Text>
          </Pressable>
        </Link>
      )
    }
    return (
      <Link
        asChild
        href={href}
        testID={linkTestID}
      >
        <Pressable
          aria-current={active ? "page" : undefined}
          style={styles[`sidebarItem-${themeName}-${active}`] ||= {
            alignItems: "center",
            backgroundColor: active ? colors.sidebarActiveBackground : "transparent",
            borderRadius: 10,
            flexDirection: "row",
            gap: 12,
            marginBottom: 4,
            paddingHorizontal: 12,
            paddingVertical: 10
          }}
          testID={testID}
        >
          <View
            style={styles.sidebarItemIcon ||= {
              alignItems: "center",
              justifyContent: "center",
              width: 20
            }}
            testID={`${testID}/icon`}
          >
            <FontAwesome6
              color={active ? colors.sidebarActiveText : colors.sidebarMutedText}
              name={icon}
              size={16}
            />
          </View>
          <Text
            style={styles[`sidebarItemText-${themeName}-${active}`] ||= {
              color: active ? colors.sidebarActiveText : colors.sidebarText,
              fontSize: 14,
              fontWeight: 600
            }}
            testID={`${testID}/label`}
          >
            {label}
          </Text>
        </Pressable>
      </Link>
    )
  }
}

/** @type {NamedExoticComponent<BoardLayoutNavItemProps>} */
const BoardLayoutNavItem = memo(shapeComponent(BoardLayoutNavItemClass))

/**
 * @typedef {object} BoardLayoutSidebarCategoryProps
 * @property {string} label Translated category label.
 * @property {boolean} marginTop Whether the category needs top spacing.
 * @property {string} testID Stable test id.
 * @property {"dark" | "light"} themeName Active theme name.
 */

/** @typedef {Record<string, never>} BoardLayoutSidebarCategoryState */

/**
 * Sidebar category label.
 * @augments {ShapeComponent<BoardLayoutSidebarCategoryProps, BoardLayoutSidebarCategoryState>}
 */
class BoardLayoutSidebarCategoryClass extends ShapeComponent {
  /**
   * Renders the category label.
   * @returns {ReactNode} Category label.
   */
  render() {
    const {label, marginTop, testID, themeName} = this.p
    const colors = buildBoardThemeColors(themeName)
    return (
      <Text
        style={styles[`sidebarCategory-${themeName}-${testID}-${marginTop}`] ||= {
          color: colors.sidebarMutedText,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.8,
          marginBottom: 6,
          marginTop: marginTop ? 12 : 0,
          paddingHorizontal: 12,
          textTransform: "uppercase"
        }}
        testID={testID}
      >
        {label}
      </Text>
    )
  }
}

/** @type {NamedExoticComponent<BoardLayoutSidebarCategoryProps>} */
const BoardLayoutSidebarCategory = memo(shapeComponent(BoardLayoutSidebarCategoryClass))

/**
 * @typedef {object} BoardLayoutBottomNavItemConfig
 * @property {string} [excludedPrefix] Prefix that must not activate this item.
 * @property {string} href Route path to open.
 * @property {FontAwesome6Name} icon FontAwesome icon name.
 * @property {string} key Stable navigation key.
 * @property {string} label Translated navigation label.
 * @property {string} [requiresAbility] Ability key required to show the entry.
 */

/**
 * @typedef {object} BoardLayoutBottomNavItemsProps
 * @property {Record<string, boolean>} abilities Current user ability map.
 * @property {string} pathname Current route pathname.
 * @property {"dark" | "light"} themeName Active theme name.
 */

/** @typedef {Record<string, never>} BoardLayoutBottomNavItemsState */

/**
 * Bottom navigation items, capped to the first five visible entries.
 * @augments {ShapeComponent<BoardLayoutBottomNavItemsProps, BoardLayoutBottomNavItemsState>}
 */
class BoardLayoutBottomNavItemsClass extends ShapeComponent {
  /** Subscribes bottom navigation labels to locale changes. */
  setup() {
    useLocale()
  }

  /**
   * Renders the visible bottom-nav items in priority order.
   * @returns {ReactNode} Bottom navigation items.
   */
  render() {
    let visibleCount = 0

    /**
     * Renders one bottom navigation item when it is visible.
     * @param {BoardLayoutBottomNavItemConfig} item Nav item config.
     * @returns {ReactNode} Bottom nav item or null.
     */
    const renderItem = ({excludedPrefix, href, icon, key, label, requiresAbility}) => {
      if (visibleCount >= 5) return null
      if (!canShowNavAbility(this.p.abilities, requiresAbility)) return null
      visibleCount += 1
      return (
        <BoardLayoutNavItem
          active={navItemActive(this.p.pathname, href, excludedPrefix)}
          href={href}
          icon={icon}
          key={key}
          label={label}
          linkTestID={`boardLayout/bottomNav/link/${key}`}
          location="bottom"
          testID={`boardLayout/bottomNav/item/${key}`}
          themeName={this.p.themeName}
        />
      )
    }
    return (
      <>
        {renderItem({href: "/timelogs/dashboard", icon: "gauge", key: "dashboard", label: _("Dashboard")})}
        {renderItem({
          href: "/projects",
          icon: "folder-open",
          key: "projects",
          label: _("Projects"),
          requiresAbility: "projects"
        })}
        {renderItem({
          href: "/tasks",
          icon: "list-check",
          key: "tasks",
          label: _("Tasks"),
          requiresAbility: "tasks"
        })}
        {renderItem({
          excludedPrefix: "/timelogs/dashboard",
          href: "/timelogs",
          icon: "clock",
          key: "timelogs",
          label: _("Timelogs"),
          requiresAbility: "timelogs"
        })}
        {renderItem({
          href: "/workstatus",
          icon: "chart-simple",
          key: "workstatus",
          label: _("Work status"),
          requiresAbility: "workstatus"
        })}
        {renderItem({
          href: "/organizations",
          icon: "building",
          key: "organizations",
          label: _("Organizations"),
          requiresAbility: "organizations"
        })}
        {renderItem({
          href: "/accounts",
          icon: "vault",
          key: "accounts",
          label: _("Accounts"),
          requiresAbility: "accounts"
        })}
        {renderItem({
          href: "/invoices",
          icon: "file-invoice",
          key: "invoices",
          label: _("Invoices"),
          requiresAbility: "invoices"
        })}
        {renderItem({
          href: "/invoice-groups",
          icon: "file-invoice-dollar",
          key: "invoice-groups",
          label: _("Invoice groups"),
          requiresAbility: "invoice-groups"
        })}
        {renderItem({
          href: "/users",
          icon: "users",
          key: "users",
          label: _("Users"),
          requiresAbility: "users"
        })}
        {renderItem({href: "/profile", icon: "user", key: "profile", label: _("Profile")})}
        {renderItem({href: "/settings", icon: "gear", key: "settings", label: _("Settings")})}
      </>
    )
  }
}

/** @type {NamedExoticComponent<BoardLayoutBottomNavItemsProps>} */
const BoardLayoutBottomNavItems = memo(shapeComponent(BoardLayoutBottomNavItemsClass))

/** @typedef {{[key: string]: unknown, children: ReactNode}} BoardLayoutProps */

/**
 * @typedef {object} BoardLayoutState
 * @property {boolean} showMobileNav Whether the mobile navigation drawer is open.
 * @property {boolean} showUserMenu Whether the user menu is open.
 * @property {boolean} signingOut Whether sign-out is running.
 */

/**
 * Shared sidebar + top-bar chrome for every Expo route.
 * @augments {ShapeComponent<BoardLayoutProps, BoardLayoutState>}
 */
class BoardLayout extends ShapeComponent {
  /** @type {ReturnType<typeof useBreakpoint>} */
  breakpoint
  /** @type {() => RouteQueryParams} */
  currentQueryParams
  /** @type {string} */
  pathname
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {CurrentUserData | null} */
  currentUser = null
  /** @type {boolean} */
  screenLoading = false
  /** @type {ReturnType<typeof usePressOutsideProps>} */
  pressOutsideProps = {}
  /** @type {ReturnType<typeof useSafeAreaInsets>} */
  safeAreaInsets = this.hookField()
  /** @type {RefObject<View | null>} */
  userMenuRef = /** @type {RefObject<View | null>} */ ({current: null})
  /** @type {number} */
  userMenuOpenedAt = 0
  /** @type {string} */
  signedOutSignInRedirectPath = "/"
  /** @type {string | null | undefined} */
  translateProviderLocale = undefined
  /** @type {BoardLayoutTranslateProviderValue | null} */
  translateProviderValue = null
  /** @type {BoardLayoutState} */
  state = {
    showMobileNav: false,
    showUserMenu: false,
    signingOut: false
  }

  /**
   * Initializes layout hooks and current-user context.
   * @returns {void} Nothing.
   */
  setup() {
    useLocale()
    const pathname = useLocationPath()
    const currentQueryParams = /** @type {() => RouteQueryParams} */ (useCurrentQueryParams())
    const {user} = useCurrentUser()

    this.breakpoint = useBreakpoint()
    this.currentQueryParams = currentQueryParams
    this.router = useRouter()
    this.pathname = pathname
    this.currentUser = user
    const projectId = optionalRouteParam(currentQueryParams(), "projectId", "Project ID")

    useProjectSyncLifecycle({appState: AppState, pathname, projectId, user})
    useProjectSyncActivityIndicator({projectId})
    boardLayoutDebugLogOnce(`setup-${pathname}-${Boolean(user)}`, "setup", {
      hasUser: Boolean(user),
      isPublicRoute: isPublicRoute(pathname),
      pathname
    })
    this.screenLoading = useAggregateScreenLoading()
    this.safeAreaInsets = useSafeAreaInsets()
    this.userMenuRef = useRef(/** @type {View | null} */ (null))
    this.pressOutsideProps = usePressOutsideProps()
    if (!user && !isPublicRoute(pathname)) {
      this.signedOutSignInRedirectPath = locationPathWithQueryParams(pathname === "/users/sign-in" ? "/" : pathname, currentQueryParams())
    }
    usePressOutside(this.userMenuRef, this.tt.onUserMenuPressOutside)

    // Locale sync from `user.locale` lives inside the current-user
    // mutators (see `syncLocaleFromCurrentUser` in `use-current-user.jsx`);
    // it fires on sign-in, profile refresh, and Settings-driven locale
    // change — never from a render path.
  }

  /**
   * Toggles the user menu. Stamps `userMenuOpenedAt` on the opening
   * press so the same press isn't immediately treated as a
   * press-outside that re-closes the menu — the trigger sits next to
   * the menu, not inside the ref's tree, so without the timestamp
   * `usePressOutside` would fire on the open press.
   * @returns {void} Nothing.
   */
  onUserMenuPress = () => {
    const next = !this.s.showUserMenu
    if (next) this.userMenuOpenedAt = Date.now()
    this.s.showUserMenu = next
  }

  /**
   * Closes the user menu when a press lands outside the menu's
   * subtree. Wired up via `outside-eye`'s `usePressOutside` so we
   * don't render a fullscreen backdrop Pressable — the backdrop
   * captured every click after the menu opened, swallowed presses on
   * the rest of the app, and forced `cursor: pointer` over the
   * entire viewport.
   * @returns {void} Nothing.
   */
  onUserMenuPressOutside = () => {
    if (!this.s.showUserMenu) return
    if (Date.now() - this.userMenuOpenedAt < 200) return
    this.s.showUserMenu = false
  }

  /** Closes transient chrome before following the brand link. */
  onBrandPress = () => {
    this.s.showUserMenu = false
    this.s.showMobileNav = false
  }

  /**
   * Toggles the mobile navigation drawer.
   * @returns {void} Nothing.
   */
  onMobileNavTogglePress = () => {
    this.s.showMobileNav = !this.s.showMobileNav
  }

  /**
   * Closes the mobile navigation drawer from the backdrop.
   * @returns {void} Nothing.
   */
  onMobileNavBackdropPress = () => {
    this.s.showMobileNav = false
  }

  /**
   * Closes the drawer after a mobile navigation item press.
   * @returns {void} Nothing.
   */
  onMobileNavItemPress = () => {
    // Explicit: pressing a mobile-nav link closes the drawer.
    // Preferred over a `useEffect(..., [pathname])` dismiss because not
    // every pathname change should close this drawer — only a user
    // intent to navigate via it.
    this.s.showMobileNav = false
  }

  /**
   * Signs out the current user.
   * @returns {Promise<void>} Resolves after sign-out handling finishes.
   */
  onSignOutPress = async () => {
    this.s.showUserMenu = false
    this.s.signingOut = true
    try {
      // deviseSignOut clears the WithCurrentUser context, which flips
      // BoardLayout to render the inline SignInForm in place of the
      // authenticated chrome — so the user stays on the current path
      // and can sign back in (as the same or a different user) without
      // a router-level redirect. Also keeps this flow compatible with
      // native Expo builds where `window.location` doesn't exist.
      await deviseSignOut()
      FlashNotifications.success(_("Signed out"))
      this.s.signingOut = false
    } catch (error) {
      this.s.signingOut = false
      FlashNotifications.errorResponse(error)
    }
  }

  /**
   * Resolves the active theme.
   * @returns {"dark" | "light"} Active theme name.
   */
  activeTheme() {
    const persisted = this.currentUser?.theme
    if (persisted === "dark" || persisted === "light") return persisted
    return detectSystemTheme()
  }

  /**
   * Resolves the active locale.
   * @returns {string} Active locale code.
   */
  activeLocale() {
    return this.currentUser?.locale || gettextConfig.getLocale() || "en"
  }

  /**
   * Toggles and persists the active theme. Updates the shared
   * current-user context optimistically so the chrome and any
   * `useThemeColors` consumer flip together; reverts to the prior
   * value if the persistence call fails.
   * @returns {Promise<void>} Resolves after theme handling finishes.
   */
  onThemeTogglePress = async () => {
    if (!this.currentUser) return
    const previousTheme = this.activeTheme()
    const nextTheme = previousTheme === "dark" ? "light" : "dark"
    updateCurrentUser({theme: nextTheme})
    try {
      await User.changeTheme({
        theme: nextTheme
      })
    } catch (error) {
      updateCurrentUser({theme: previousTheme})
      FlashNotifications.errorResponse(error)
    }
  }

  /**
   * Applies and persists a locale selection.
   * @param {string} locale Locale code to apply.
   * @returns {Promise<void>} Resolves after locale handling finishes.
   */
  onLocaleChange = async (locale) => {
    if (locale === this.activeLocale()) {
      this.s.showUserMenu = false
      return
    }
    this.s.showUserMenu = false
    if (!this.currentUser) {
      // No current user, so there's nothing to persist server-side.
      // applyLocale still mirrors the choice into the Option store so
      // a window refresh starts from this value instead of falling
      // back to the browser language.
      void applyLocale(locale)
      return
    }
    try {
      await User.changeLocale({
        locale
      })
      // `updateCurrentUser` invokes syncLocaleFromCurrentUser, which
      // calls gettextConfig.setLocale for us — no explicit setLocale
      // here.
      updateCurrentUser({locale})
    } catch (error) {
      FlashNotifications.errorResponse(error)
    }
  }

  /** Restores the route that rendered the inline sign-in form. */
  onSignedOutSignInSuccess = () => {
    this.router.replace(this.signedOutSignInRedirectPath)
  }

  /**
   * Checks whether a navigation item should be visible.
   * @param {string} [requiresAbility] Ability key required by the item.
   * @returns {boolean} True when the item is visible.
   */
  canShowNavItem(requiresAbility) {
    return canShowNavAbility(this.currentUser?.abilities ?? {}, requiresAbility)
  }

  /**
   * Checks whether any ability in a category is visible.
   * @param {string[]} abilityKeys Ability keys in the category.
   * @returns {boolean} True when at least one item can render.
   */
  canShowAnyNavItem(abilityKeys) {
    return abilityKeys.some((abilityKey) => this.tt.canShowNavItem(abilityKey))
  }

  /**
   * Checks whether a navigation item owns the active route.
   * @param {string} href Route path represented by the item.
   * @param {string} [excludedPrefix] Prefix that must not activate this item.
   * @returns {boolean} True when active.
   */
  navItemActive(href, excludedPrefix) {
    return navItemActive(this.tt.pathname, href, excludedPrefix)
  }

  /**
   * Renders the board layout chrome.
   * @returns {ReactNode} Layout content.
   */
  render() {
    const {breakpoint, pathname} = this.tt
    const {showMobileNav, showUserMenu, signingOut} = this.s
    const currentUser = this.currentUser
    const currentLocale = this.activeLocale()
    const themeName = this.activeTheme()
    const isWide = breakpoint.mdUp
    const colors = buildBoardThemeColors(themeName)
    const userMenuLabel = currentUser
      ? currentUser.name || currentUser.email
      : _("Account")
    const abilities = currentUser?.abilities ?? {}
    const safeAreaInsets = this.tt.safeAreaInsets
    const shouldUseBodyScroll = shouldScrollBody(pathname)
    const shouldUseFullBleedScroll = shouldUseFullBleedBodyScroll(pathname)
    let translateValue = this.translateProviderValue
    if (!translateValue || this.translateProviderLocale !== currentLocale) {
      translateValue = {locale: currentLocale}
      this.translateProviderLocale = currentLocale
      this.translateProviderValue = translateValue
    }

    boardLayoutDebugLogOnce(`render-${pathname}-${Boolean(currentUser)}-${this.screenLoading}-${shouldUseBodyScroll}-${shouldUseFullBleedScroll}`, "render", {
      hasUser: Boolean(currentUser),
      isPublicRoute: isPublicRoute(pathname),
      pathname,
      screenLoading: this.screenLoading,
      shouldUseFullBleedScroll,
      shouldUseBodyScroll
    })

    if (!currentUser && isSignInRoute(pathname)) {
      return (
        <PropforgeView
          dataSet={dataSets.signInRouteSystemTestingComponent ||= {
            focussed: "true"
          }}
          style={styles[`signInRouteSystemTestingComponent-${themeName}-${safeAreaInsets.top}-${safeAreaInsets.bottom}`] ||= {
            backgroundColor: colors.screen,
            flex: 1,
            paddingBottom: safeAreaInsets.bottom,
            paddingTop: safeAreaInsets.top
          }}
          testID="systemTestingComponent"
        >
          <FlashNotificationsContainer />
          <TranslateContext.Provider
            value={translateValue}
          >
            <WithDefaultStyle
              style={styles[`signInRouteTextDefault-${themeName}`] ||= {
                Link: {
                  color: colors.text,
                  fontSize: 14
                },
                Text: {
                  color: colors.text,
                  fontSize: 14
                },
                TextInput: {
                  color: colors.text,
                  fontSize: 14
                }
              }}
            >
              {this.p.children}
            </WithDefaultStyle>
          </TranslateContext.Provider>
        </PropforgeView>
      )
    }

    // If there is no signed-in user, render the sign-in form inline
    // instead of the authenticated chrome unless the route is public.
    // Waiting for the current-profile fetch to resolve is handled by _layout.jsx's
    // outer loading gate, so by the time BoardLayout mounts the
    // WithCurrentUser context is guaranteed to be loaded.
    if (!currentUser && !isPublicRoute(pathname)) {
      return (
        <PropforgeView
          dataSet={dataSets.signedOutSystemTestingComponent ||= {
            focussed: "true"
          }}
          style={styles[`signedOutSystemTestingComponent-${themeName}-${safeAreaInsets.top}-${safeAreaInsets.bottom}`] ||= {
            backgroundColor: colors.screen,
            flex: 1,
            paddingBottom: safeAreaInsets.bottom,
            paddingTop: safeAreaInsets.top
          }}
          testID="systemTestingComponent"
        >
          <FlashNotificationsContainer />
          <SignInForm onSignInSuccess={this.tt.onSignedOutSignInSuccess} />
        </PropforgeView>
      )
    }

    return (
      <PropforgeView
        dataSet={dataSets.rootSystemTestingComponent ||= {
          focussed: "true"
        }}
        style={styles.systemTestingComponent ||= {
          flex: 1
        }}
        testID="systemTestingComponent"
      >
      <View
        {...this.pressOutsideProps}
        style={styles.root ||= {
          flex: 1,
          flexDirection: "row"
        }}
        testID="boardLayout"
      >
        <FlashNotificationsContainer />
        <ConfirmModalHost />
        {isWide &&
          <View
            style={styles[`sidebar-${themeName}-${safeAreaInsets.top}`] ||= {
              backgroundColor: colors.sidebar,
              borderRightColor: colors.sidebarBorder,
              borderRightWidth: 1,
              paddingBottom: 12,
              paddingTop: 16 + safeAreaInsets.top,
              width: BOARD_LAYOUT_SIDEBAR_WIDTH
            }}
            testID="boardLayout/sidebar"
          >
            <Link asChild href="/">
              <Pressable
                onPress={this.tt.onBrandPress}
                style={styles.sidebarBrand ||= {
                  alignItems: "center",
                  flexDirection: "row",
                  gap: 10,
                  marginBottom: 24,
                  paddingHorizontal: 16
                }}
                testID="boardLayout/sidebar/brand"
              >
                <View
                  style={styles[`sidebarBrandIcon-${themeName}`] ||= {
                    alignItems: "center",
                    backgroundColor: colors.primary,
                    borderRadius: 10,
                    height: 32,
                    justifyContent: "center",
                    width: 32
                  }}
                  testID="boardLayout/sidebar/brand/icon"
                >
                  <FontAwesome6
                    color={colors.onPrimary}
                    name="list-check"
                    size={16}
                  />
                </View>
                <Text
                  style={styles[`sidebarBrandText-${themeName}`] ||= {
                    color: colors.sidebarText,
                    fontSize: 16,
                    fontWeight: 700,
                    letterSpacing: -0.2
                  }}
                  testID="boardLayout/sidebar/brand/text"
                >
                  {_("Awesome Tasks")}
                </Text>
              </Pressable>
            </Link>
            <ScrollView
              contentContainerStyle={styles.sidebarNavContent ||= {
                paddingBottom: 12,
                paddingHorizontal: 12,
                paddingTop: 4
              }}
              showsVerticalScrollIndicator={false}
              style={styles.sidebarNavScroll ||= {
                flex: 1,
                minHeight: 0
              }}
              testID="boardLayout/sidebar/nav"
            >
              <BoardLayoutSidebarCategory
                label={_("Work")}
                marginTop={false}
                testID="boardLayout/sidebar/category/work"
                themeName={themeName}
              />
              <BoardLayoutNavItem
                active={this.tt.navItemActive("/timelogs/dashboard")}
                href="/timelogs/dashboard"
                icon="gauge"
                label={_("Dashboard")}
                linkTestID="boardLayout/sidebar/link/dashboard"
                location="sidebar"
                testID="boardLayout/sidebar/item/dashboard"
                themeName={themeName}
              />
              {this.tt.canShowNavItem("projects") &&
                <BoardLayoutNavItem
                  active={this.tt.navItemActive("/projects")}
                  href="/projects"
                  icon="folder-open"
                  label={_("Projects")}
                  linkTestID="boardLayout/sidebar/link/projects"
                  location="sidebar"
                  testID="boardLayout/sidebar/item/projects"
                  themeName={themeName}
                />
              }
              {this.tt.canShowNavItem("tasks") &&
                <BoardLayoutNavItem
                  active={this.tt.navItemActive("/tasks")}
                  href="/tasks"
                  icon="list-check"
                  label={_("Tasks")}
                  linkTestID="boardLayout/sidebar/link/tasks"
                  location="sidebar"
                  testID="boardLayout/sidebar/item/tasks"
                  themeName={themeName}
                />
              }
              {this.tt.canShowAnyNavItem(["timelogs", "workstatus"]) &&
                <BoardLayoutSidebarCategory
                  label={_("Time")}
                  marginTop={true}
                  testID="boardLayout/sidebar/category/time"
                  themeName={themeName}
                />
              }
              {this.tt.canShowNavItem("timelogs") &&
                <BoardLayoutNavItem
                  active={this.tt.navItemActive("/timelogs", "/timelogs/dashboard")}
                  href="/timelogs"
                  icon="clock"
                  label={_("Timelogs")}
                  linkTestID="boardLayout/sidebar/link/timelogs"
                  location="sidebar"
                  testID="boardLayout/sidebar/item/timelogs"
                  themeName={themeName}
                />
              }
              {this.tt.canShowNavItem("workstatus") &&
                <BoardLayoutNavItem
                  active={this.tt.navItemActive("/workstatus")}
                  href="/workstatus"
                  icon="chart-simple"
                  label={_("Work status")}
                  linkTestID="boardLayout/sidebar/link/workstatus"
                  location="sidebar"
                  testID="boardLayout/sidebar/item/workstatus"
                  themeName={themeName}
                />
              }
              {this.tt.canShowAnyNavItem(["organizations", "accounts", "invoices", "invoice-groups"]) &&
                <BoardLayoutSidebarCategory
                  label={_("Invoicing")}
                  marginTop={true}
                  testID="boardLayout/sidebar/category/invoicing"
                  themeName={themeName}
                />
              }
              {this.tt.canShowNavItem("organizations") &&
                <BoardLayoutNavItem
                  active={this.tt.navItemActive("/organizations")}
                  href="/organizations"
                  icon="building"
                  label={_("Organizations")}
                  linkTestID="boardLayout/sidebar/link/organizations"
                  location="sidebar"
                  testID="boardLayout/sidebar/item/organizations"
                  themeName={themeName}
                />
              }
              {this.tt.canShowNavItem("accounts") &&
                <BoardLayoutNavItem
                  active={this.tt.navItemActive("/accounts")}
                  href="/accounts"
                  icon="vault"
                  label={_("Accounts")}
                  linkTestID="boardLayout/sidebar/link/accounts"
                  location="sidebar"
                  testID="boardLayout/sidebar/item/accounts"
                  themeName={themeName}
                />
              }
              {this.tt.canShowNavItem("invoices") &&
                <BoardLayoutNavItem
                  active={this.tt.navItemActive("/invoices")}
                  href="/invoices"
                  icon="file-invoice"
                  label={_("Invoices")}
                  linkTestID="boardLayout/sidebar/link/invoices"
                  location="sidebar"
                  testID="boardLayout/sidebar/item/invoices"
                  themeName={themeName}
                />
              }
              {this.tt.canShowNavItem("invoice-groups") &&
                <BoardLayoutNavItem
                  active={this.tt.navItemActive("/invoice-groups")}
                  href="/invoice-groups"
                  icon="file-invoice-dollar"
                  label={_("Invoice groups")}
                  linkTestID="boardLayout/sidebar/link/invoice-groups"
                  location="sidebar"
                  testID="boardLayout/sidebar/item/invoice-groups"
                  themeName={themeName}
                />
              }
              <BoardLayoutSidebarCategory
                label={_("Account")}
                marginTop={true}
                testID="boardLayout/sidebar/category/account"
                themeName={themeName}
              />
              {this.tt.canShowNavItem("users") &&
                <BoardLayoutNavItem
                  active={this.tt.navItemActive("/users")}
                  href="/users"
                  icon="users"
                  label={_("Users")}
                  linkTestID="boardLayout/sidebar/link/users"
                  location="sidebar"
                  testID="boardLayout/sidebar/item/users"
                  themeName={themeName}
                />
              }
              <BoardLayoutNavItem
                active={this.tt.navItemActive("/profile")}
                href="/profile"
                icon="user"
                label={_("Profile")}
                linkTestID="boardLayout/sidebar/link/profile"
                location="sidebar"
                testID="boardLayout/sidebar/item/profile"
                themeName={themeName}
              />
              <BoardLayoutNavItem
                active={this.tt.navItemActive("/settings")}
                href="/settings"
                icon="gear"
                label={_("Settings")}
                linkTestID="boardLayout/sidebar/link/settings"
                location="sidebar"
                testID="boardLayout/sidebar/item/settings"
                themeName={themeName}
              />
            </ScrollView>
            <Pressable
              onPress={this.tt.onThemeTogglePress}
              style={styles[`sidebarThemeToggle-${themeName}`] ||= {
                alignItems: "center",
                backgroundColor:
                  themeName === "dark"
                    ? "rgba(96, 165, 250, 0.18)"
                    : "rgba(29, 78, 216, 0.10)",
                borderRadius: 10,
                flexDirection: "row",
                gap: 10,
                marginHorizontal: 12,
                marginTop: 8,
                paddingHorizontal: 12,
                paddingVertical: 10
              }}
              testID="boardLayout/sidebar/themeToggle"
            >
              <FontAwesome6
                color={colors.sidebarActiveText}
                name={themeName === "dark" ? "sun" : "moon"}
                size={14}
              />
              <Text
                style={styles[`sidebarThemeToggleText-${themeName}`] ||= {
                  color: colors.sidebarActiveText,
                  fontSize: 13,
                  fontWeight: 700
                }}
                testID="boardLayout/sidebar/themeToggle/label"
              >
                {themeName === "dark" ? _("Light theme") : _("Dark theme")}
              </Text>
            </Pressable>
          </View>
        }
        <View
          style={styles.main ||= {
            flex: 1,
            // Without `minHeight: 0` the flex column grows to its children's intrinsic height; with body `overflow: hidden` the inner ScrollView never sees the overflow and the bottom is clipped.
            minHeight: 0,
            minWidth: 0
          }}
          testID="boardLayout/main"
        >
          <View
            style={styles[`topBar-${themeName}-${safeAreaInsets.top}`] ||= {
              alignItems: "center",
              backgroundColor: colors.topBar,
              borderBottomColor: colors.topBarBorder,
              borderBottomWidth: 1,
              flexDirection: "row",
              gap: 12,
              justifyContent: "space-between",
              paddingHorizontal: 20,
              paddingBottom: 14,
              paddingTop: 14 + safeAreaInsets.top,
              position: "relative",
              zIndex: 20
            }}
            testID="boardLayout/topBar"
          >
            <View
              style={styles.topBarTitleRow ||= {
                alignItems: "center",
                flexDirection: "row",
                gap: 10
              }}
              testID="boardLayout/topBar/titleRow"
            >
              {!isWide &&
                <Pressable
                  onPress={this.tt.onMobileNavTogglePress}
                  style={styles.mobileNavToggle ||= {
                    alignItems: "center",
                    backgroundColor: "#f1f5f9",
                    borderRadius: 8,
                    height: 32,
                    justifyContent: "center",
                    width: 32
                  }}
                  testID="boardLayout/mobileNavToggle"
                >
                  <FontAwesome6 color="#475569" name="bars" size={14} />
                </Pressable>
              }
              <Link asChild href="/">
                <Pressable
                  onPress={this.tt.onBrandPress}
                  style={styles.topBarBrand ||= {
                    alignItems: "center",
                    flexDirection: "row",
                    gap: 10
                  }}
                  testID="boardLayout/topBar/brand"
                >
                  {!isWide &&
                    <View
                      style={styles.topBarIcon ||= {
                        alignItems: "center",
                        backgroundColor: "#1d4ed8",
                        borderRadius: 8,
                        height: 28,
                        justifyContent: "center",
                        width: 28
                      }}
                      testID="boardLayout/topBar/icon"
                    >
                      <FontAwesome6 color="#ffffff" name="list-check" size={14} />
                    </View>
                  }
                  <Text
                    style={styles[`topBarTitle-${themeName}`] ||= {
                      color: colors.topBarText,
                      fontSize: 18,
                      fontWeight: 700,
                      letterSpacing: -0.2
                    }}
                    testID="boardLayout/topBar/title"
                  >
                    {_("Awesome Tasks")}
                  </Text>
                </Pressable>
              </Link>
            </View>
            <View
              style={styles.topBarRight ||= {
                alignItems: "center",
                flexDirection: "row",
                gap: 8,
                position: "relative"
              }}
              testID="boardLayout/topBar/right"
            >
              {currentUser && <ProjectSyncStatusIndicator />}
              <Pressable
                accessibilityLabel={userMenuLabel}
                onPress={this.tt.onUserMenuPress}
                style={styles[`userMenuTrigger-${themeName}`] ||= {
                  alignItems: "center",
                  backgroundColor: colors.surfaceMuted,
                  borderRadius: 999,
                  flexDirection: "row",
                  gap: 8,
                  paddingHorizontal: 12,
                  paddingVertical: 8
                }}
                testID="boardLayout/userMenuTrigger"
              >
                <FontAwesome6 color={colors.textMuted} name="user" size={14} />
                {isWide &&
                  <Text
                    style={styles[`userMenuTriggerText-${themeName}`] ||= {
                      color: colors.text,
                      fontSize: 13,
                      fontWeight: 700
                    }}
                    testID="boardLayout/userMenuTrigger/label"
                  >
                    {userMenuLabel}
                  </Text>
                }
                <FontAwesome6 color={colors.placeholder} name="chevron-down" size={10} />
              </Pressable>
              {showUserMenu &&
                <View
                  ref={this.userMenuRef}
                  style={styles[`userMenu-${themeName}`] ||= {
                    backgroundColor: colors.surfaceRaised,
                    borderColor: colors.borderStrong,
                    borderRadius: 12,
                    borderWidth: 1,
                    minWidth: 220,
                    paddingVertical: 6,
                    position: "absolute",
                    right: 0,
                    shadowColor: "#0f172a",
                    shadowOffset: {
                      height: 8,
                      width: 0
                    },
                    shadowOpacity: 0.12,
                    shadowRadius: 24,
                    top: 44,
                    zIndex: 30
                  }}
                  testID="boardLayout/userMenu"
                >
                  {currentUser &&
                    <View
                      style={styles[`userMenuHeader-${themeName}`] ||= {
                        borderBottomColor: colors.divider,
                        borderBottomWidth: 1,
                        gap: 2,
                        paddingBottom: 8,
                        paddingHorizontal: 14,
                        paddingTop: 6
                      }}
                      testID="boardLayout/userMenu/header"
                    >
                      <Text
                        style={styles[`userMenuName-${themeName}`] ||= {
                          color: colors.text,
                          fontSize: 13,
                          fontWeight: 700
                        }}
                        testID="boardLayout/userMenu/name"
                      >
                        {currentUser.name}
                      </Text>
                      <Text
                        style={styles[`userMenuEmail-${themeName}`] ||= {
                          color: colors.textMuted,
                          fontSize: 12,
                          fontWeight: 500
                        }}
                        testID="boardLayout/userMenu/email"
                      >
                        {currentUser.email}
                      </Text>
                    </View>
                  }
                  <Link
                    asChild
                    href="/profile"
                    testID="boardLayout/userMenu/profileLink"
                  >
                    <Pressable
                      onPress={this.tt.onUserMenuPress}
                      style={styles.userMenuItem ||= {
                        alignItems: "center",
                        flexDirection: "row",
                        gap: 10,
                        paddingHorizontal: 14,
                        paddingVertical: 10
                      }}
                      testID="boardLayout/userMenu/profileItem"
                    >
                      <FontAwesome6 color={colors.textMuted} name="user-pen" size={13} />
                      <Text
                        style={styles[`userMenuItemText-${themeName}`] ||= {
                          color: colors.text,
                          fontSize: 13,
                          fontWeight: 600
                        }}
                        testID="boardLayout/userMenu/profileItem/label"
                      >
                        {_("Edit profile")}
                      </Text>
                    </Pressable>
                  </Link>
                  <View
                    style={styles[`userMenuLocaleRow-${themeName}`] ||= {
                      borderTopColor: colors.divider,
                      borderTopWidth: 1,
                      flexDirection: "row",
                      gap: 6,
                      marginTop: 4,
                      paddingHorizontal: 14,
                      paddingVertical: 10
                    }}
                    testID="boardLayout/userMenu/localeRow"
                  >
                    {LOCALE_OPTIONS.map((option) => {
                      const active = option.value === currentLocale
                      return (
                        <Pressable
                          key={option.value}
                          onPress={() => this.tt.onLocaleChange(option.value)}
                          style={styles[`localeChip-${themeName}-${option.value}-${active}`] ||= {
                              alignItems: "center",
                              backgroundColor: active ? colors.primary : colors.surfaceMuted,
                              borderRadius: 999,
                              flexDirection: "row",
                              paddingHorizontal: 10,
                              paddingVertical: 4
                          }}
                          testID={`boardLayout/userMenu/locale/${option.value}`}
                        >
                          <View
                            style={styles.localeChipIcon ||= {
                              marginRight: 6,
                              width: 12
                            }}
                            testID={`boardLayout/userMenu/locale/${option.value}/icon`}
                          >
                            <FontAwesome6
                              color={active ? colors.onPrimary : colors.textMuted}
                              name="language"
                              size={11}
                            />
                          </View>
                          <Text
                            style={styles[`localeChipText-${themeName}-${option.value}-${active}`] ||= {
                              color: active ? colors.onPrimary : colors.textMuted,
                              fontSize: 11,
                              fontWeight: 700
                            }}
                            testID={`boardLayout/userMenu/locale/${option.value}/label`}
                          >
                            {option.label}
                          </Text>
                        </Pressable>
                      )
                    })}
                  </View>
                  {currentUser &&
                    <Pressable
                      disabled={signingOut}
                      onPress={this.tt.onSignOutPress}
                      style={styles[`userMenuSignOut-${themeName}-${signingOut}`] ||= {
                        alignItems: "center",
                        borderTopColor: colors.divider,
                        borderTopWidth: 1,
                        flexDirection: "row",
                        gap: 10,
                        paddingHorizontal: 14,
                        paddingVertical: 10
                      }}
                      testID="boardLayout/userMenu/signOut"
                    >
                      <FontAwesome6
                        color={colors.dangerText}
                        name="arrow-right-from-bracket"
                        size={13}
                      />
                      <Text
                        style={styles[`userMenuSignOutText-${themeName}`] ||= {
                          color: colors.dangerText,
                          fontSize: 13,
                          fontWeight: 700
                        }}
                        testID="boardLayout/userMenu/signOut/label"
                      >
                        {signingOut ? _("Signing out...") : _("Sign out")}
                      </Text>
                    </Pressable>
                  }
                  {!currentUser &&
                    <View
                      style={styles[`userMenuSignedOut-${themeName}`] ||= {
                        borderTopColor: colors.divider,
                        borderTopWidth: 1,
                        paddingHorizontal: 14,
                        paddingVertical: 10
                      }}
                      testID="boardLayout/userMenu/signedOut"
                    >
                      <Text
                        style={styles[`userMenuSignedOutText-${themeName}`] ||= {
                          color: colors.textMuted,
                          fontSize: 12,
                          fontWeight: 500
                        }}
                        testID="boardLayout/userMenu/signedOut/label"
                      >
                        {_("Not signed in")}
                      </Text>
                    </View>
                  }
                </View>
              }
            </View>
          </View>
          <View
            style={styles[`content-${themeName}`] ||= {
              backgroundColor: colors.screen,
              flex: 1,
              minHeight: 0,
              minWidth: 0
            }}
            testID="boardLayout/content"
          >
            <TranslateContext.Provider
              value={translateValue}
            >
              <WithDefaultStyle
                style={styles[`textDefault-${themeName}`] ||= {
                  Link: {
                    color: colors.text,
                    fontSize: 14
                  },
                  Text: {
                    color: colors.text,
                    fontSize: 14
                  },
                  TextInput: {
                    color: colors.text,
                    fontSize: 14
                  }
                }}
              >
                {shouldUseBodyScroll && shouldUseFullBleedScroll &&
                  <ScrollView
                    contentContainerStyle={styles.contentScrollInnerFullBleed ||= {
                      flexGrow: 1,
                      width: "100%"
                    }}
                    style={styles.contentScroll ||= {
                      flex: 1,
                      minWidth: 0,
                      width: "100%"
                    }}
                    testID="boardLayout/content/scroll"
                  >
                    {this.p.children}
                  </ScrollView>
                }
                {shouldUseBodyScroll && !shouldUseFullBleedScroll &&
                  <ScrollView
                    contentContainerStyle={styles[`contentScrollInner-${isWide}`] ||= breakpoint.styling({
                      base: {
                        flexGrow: 1,
                        paddingBottom: 0,
                        paddingLeft: 24,
                        paddingRight: 24,
                        paddingTop: 24
                      },
                      mdUp: {
                        flexGrow: 1,
                        paddingBottom: 48,
                        paddingLeft: 24,
                        paddingRight: 24,
                        paddingTop: 24
                      }
                    })}
                    style={styles.contentScroll ||= {
                      flex: 1,
                      minWidth: 0,
                      width: "100%"
                    }}
                    testID="boardLayout/content/scroll"
                  >
                    {this.p.children}
                  </ScrollView>
                }
                {!shouldUseBodyScroll && this.p.children}
              </WithDefaultStyle>
            </TranslateContext.Provider>
            {this.screenLoading &&
              <View
                pointerEvents="none"
                style={styles[`screenLoadingOverlay-${themeName}`] ||= {
                  alignItems: "center",
                  backgroundColor: colors.screen,
                  bottom: 0,
                  justifyContent: "center",
                  left: 0,
                  opacity: 0.85,
                  position: "absolute",
                  right: 0,
                  top: 0
                }}
                testID="boardLayout/screenLoading"
              >
                <LoadingState testID="boardLayout/screenLoading/label" />
              </View>
            }
          </View>
          {!isWide &&
            <View
              style={styles[`bottomNav-${themeName}-${safeAreaInsets.bottom}`] ||= {
                alignItems: "center",
                backgroundColor: colors.bottomNav,
                borderTopColor: colors.bottomNavBorder,
                borderTopWidth: 1,
                flexDirection: "row",
                gap: 4,
                justifyContent: "space-around",
                paddingBottom: 6 + safeAreaInsets.bottom,
                paddingHorizontal: 6,
                paddingTop: 6
              }}
              testID="boardLayout/bottomNav"
            >
              <BoardLayoutBottomNavItems
                abilities={abilities}
                pathname={pathname}
                themeName={themeName}
              />
            </View>
          }
        </View>
        {!isWide && showMobileNav &&
          <Pressable
            onPress={this.tt.onMobileNavBackdropPress}
            style={styles.mobileNavBackdrop ||= {
              backgroundColor: "rgba(15, 23, 42, 0.6)",
              bottom: 0,
              left: 0,
              position: "absolute",
              right: 0,
              top: 0,
              zIndex: 40
            }}
            testID="boardLayout/mobileNavBackdrop"
          />
        }
        {!isWide && showMobileNav &&
          <View
            style={styles[`mobileNavDrawer-${safeAreaInsets.top}-${safeAreaInsets.bottom}`] ||= {
              backgroundColor: "#0f172a",
              bottom: 0,
              left: 0,
              maxWidth: 320,
              paddingBottom: 16 + safeAreaInsets.bottom,
              paddingTop: 16 + safeAreaInsets.top,
              position: "absolute",
              top: 0,
              width: "85%",
              zIndex: 50
            }}
            testID="boardLayout/mobileNavDrawer"
          >
            <View
              style={styles.mobileNavBrand ||= {
                alignItems: "center",
                flexDirection: "row",
                gap: 10,
                marginBottom: 16,
                paddingHorizontal: 16
              }}
              testID="boardLayout/mobileNavDrawer/brand"
            >
              <View
                style={styles.mobileNavBrandIcon ||= {
                  alignItems: "center",
                  backgroundColor: "#1d4ed8",
                  borderRadius: 10,
                  height: 32,
                  justifyContent: "center",
                  width: 32
                }}
                testID="boardLayout/mobileNavDrawer/brand/icon"
              >
                <FontAwesome6 color="#ffffff" name="list-check" size={16} />
              </View>
              <Text
                style={styles.mobileNavBrandText ||= {
                  color: "#f8fafc",
                  flex: 1,
                  fontSize: 16,
                  fontWeight: 700,
                  letterSpacing: -0.2
                }}
                testID="boardLayout/mobileNavDrawer/brand/text"
              >
                {_("Awesome Tasks")}
              </Text>
              <Pressable
                onPress={this.tt.onMobileNavBackdropPress}
                style={styles.mobileNavCloseButton ||= {
                  alignItems: "center",
                  height: 32,
                  justifyContent: "center",
                  width: 32
                }}
                testID="boardLayout/mobileNavDrawer/closeButton"
              >
                <FontAwesome6 color="#94a3b8" name="xmark" size={16} />
              </Pressable>
            </View>
            <ScrollView
              contentContainerStyle={styles.mobileNavContent ||= {
                paddingBottom: 12,
                paddingHorizontal: 12,
                paddingTop: 4
              }}
              showsVerticalScrollIndicator={false}
              style={styles.mobileNavScroll ||= {
                flex: 1,
                minHeight: 0
              }}
              testID="boardLayout/mobileNavDrawer/scroll"
            >
              <BoardLayoutNavItem
                active={this.tt.navItemActive("/timelogs/dashboard")}
                href="/timelogs/dashboard"
                icon="gauge"
                label={_("Dashboard")}
                linkTestID="boardLayout/mobileNavDrawer/link/dashboard"
                location="mobile"
                onPress={this.tt.onMobileNavItemPress}
                testID="boardLayout/mobileNavDrawer/item/dashboard"
                themeName={themeName}
              />
              {this.tt.canShowNavItem("projects") &&
                <BoardLayoutNavItem
                  active={this.tt.navItemActive("/projects")}
                  href="/projects"
                  icon="folder-open"
                  label={_("Projects")}
                  linkTestID="boardLayout/mobileNavDrawer/link/projects"
                  location="mobile"
                  onPress={this.tt.onMobileNavItemPress}
                  testID="boardLayout/mobileNavDrawer/item/projects"
                  themeName={themeName}
                />
              }
              {this.tt.canShowNavItem("tasks") &&
                <BoardLayoutNavItem
                  active={this.tt.navItemActive("/tasks")}
                  href="/tasks"
                  icon="list-check"
                  label={_("Tasks")}
                  linkTestID="boardLayout/mobileNavDrawer/link/tasks"
                  location="mobile"
                  onPress={this.tt.onMobileNavItemPress}
                  testID="boardLayout/mobileNavDrawer/item/tasks"
                  themeName={themeName}
                />
              }
              {this.tt.canShowNavItem("timelogs") &&
                <BoardLayoutNavItem
                  active={this.tt.navItemActive("/timelogs", "/timelogs/dashboard")}
                  href="/timelogs"
                  icon="clock"
                  label={_("Timelogs")}
                  linkTestID="boardLayout/mobileNavDrawer/link/timelogs"
                  location="mobile"
                  onPress={this.tt.onMobileNavItemPress}
                  testID="boardLayout/mobileNavDrawer/item/timelogs"
                  themeName={themeName}
                />
              }
              {this.tt.canShowNavItem("workstatus") &&
                <BoardLayoutNavItem
                  active={this.tt.navItemActive("/workstatus")}
                  href="/workstatus"
                  icon="chart-simple"
                  label={_("Work status")}
                  linkTestID="boardLayout/mobileNavDrawer/link/workstatus"
                  location="mobile"
                  onPress={this.tt.onMobileNavItemPress}
                  testID="boardLayout/mobileNavDrawer/item/workstatus"
                  themeName={themeName}
                />
              }
              {this.tt.canShowNavItem("organizations") &&
                <BoardLayoutNavItem
                  active={this.tt.navItemActive("/organizations")}
                  href="/organizations"
                  icon="building"
                  label={_("Organizations")}
                  linkTestID="boardLayout/mobileNavDrawer/link/organizations"
                  location="mobile"
                  onPress={this.tt.onMobileNavItemPress}
                  testID="boardLayout/mobileNavDrawer/item/organizations"
                  themeName={themeName}
                />
              }
              {this.tt.canShowNavItem("accounts") &&
                <BoardLayoutNavItem
                  active={this.tt.navItemActive("/accounts")}
                  href="/accounts"
                  icon="vault"
                  label={_("Accounts")}
                  linkTestID="boardLayout/mobileNavDrawer/link/accounts"
                  location="mobile"
                  onPress={this.tt.onMobileNavItemPress}
                  testID="boardLayout/mobileNavDrawer/item/accounts"
                  themeName={themeName}
                />
              }
              {this.tt.canShowNavItem("invoices") &&
                <BoardLayoutNavItem
                  active={this.tt.navItemActive("/invoices")}
                  href="/invoices"
                  icon="file-invoice"
                  label={_("Invoices")}
                  linkTestID="boardLayout/mobileNavDrawer/link/invoices"
                  location="mobile"
                  onPress={this.tt.onMobileNavItemPress}
                  testID="boardLayout/mobileNavDrawer/item/invoices"
                  themeName={themeName}
                />
              }
              {this.tt.canShowNavItem("invoice-groups") &&
                <BoardLayoutNavItem
                  active={this.tt.navItemActive("/invoice-groups")}
                  href="/invoice-groups"
                  icon="file-invoice-dollar"
                  label={_("Invoice groups")}
                  linkTestID="boardLayout/mobileNavDrawer/link/invoice-groups"
                  location="mobile"
                  onPress={this.tt.onMobileNavItemPress}
                  testID="boardLayout/mobileNavDrawer/item/invoice-groups"
                  themeName={themeName}
                />
              }
              {this.tt.canShowNavItem("users") &&
                <BoardLayoutNavItem
                  active={this.tt.navItemActive("/users")}
                  href="/users"
                  icon="users"
                  label={_("Users")}
                  linkTestID="boardLayout/mobileNavDrawer/link/users"
                  location="mobile"
                  onPress={this.tt.onMobileNavItemPress}
                  testID="boardLayout/mobileNavDrawer/item/users"
                  themeName={themeName}
                />
              }
              <BoardLayoutNavItem
                active={this.tt.navItemActive("/profile")}
                href="/profile"
                icon="user"
                label={_("Profile")}
                linkTestID="boardLayout/mobileNavDrawer/link/profile"
                location="mobile"
                onPress={this.tt.onMobileNavItemPress}
                testID="boardLayout/mobileNavDrawer/item/profile"
                themeName={themeName}
              />
              <BoardLayoutNavItem
                active={this.tt.navItemActive("/settings")}
                href="/settings"
                icon="gear"
                label={_("Settings")}
                linkTestID="boardLayout/mobileNavDrawer/link/settings"
                location="mobile"
                onPress={this.tt.onMobileNavItemPress}
                testID="boardLayout/mobileNavDrawer/item/settings"
                themeName={themeName}
              />
            </ScrollView>
          </View>
        }
      </View>
      </PropforgeView>
    )
  }
}

/** @type {NamedExoticComponent<BoardLayoutProps>} */
const BoardLayoutComponent = memo(shapeComponent(BoardLayout))

export default BoardLayoutComponent
